import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AppButton, AppCard } from '@/components/atoms';

export type AsideCalloutProps = {
  title: string;
  text: string;
  /** The link's own words. Never "click here": it is read out of context. */
  label: string;
  /** A site path. External destinations are not this component's job. */
  to: string;
};

/**
 * A quiet card that sends the reader somewhere else.
 *
 * The pattern a page needs when it deliberately does NOT restate something:
 * one heading, one sentence of what is over there, one link. Two pages reached
 * for it in the same change — the consultation band on /contact pointing at
 * the page with the form's full story, and that page pointing back at the
 * service page that argues for a consultation — which is the moment to share
 * it rather than write the card twice.
 *
 * A `ghost` button, not an outline and not a second filled one. Every band
 * that wants this card already has its own primary action, and IA §5 allows
 * one per screen: an outline here would compete for the glance at exactly the
 * moment the band is asking for the commitment. `px: 0` pulls it back to the
 * text's left edge, so it reads as the end of the sentence rather than as a
 * control that happens to be below it.
 *
 * `surfaceVariant` is the one token that is a step off the page in both
 * schemes — #F2EFEF on light, #242122 on dark — so the card recedes without
 * either value being named here.
 */
export function AsideCallout({ title, text, label, to }: AsideCalloutProps) {
  return (
    <AppCard sx={{ p: { xs: 5, md: 6 }, bgcolor: 'surfaceVariant' }}>
      <Stack spacing={3} alignItems="flex-start">
        <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
        <AppButton
          to={to}
          appVariant="ghost"
          size="small"
          endIcon={<ArrowForwardRounded />}
          sx={{ px: 0 }}
        >
          {label}
        </AppButton>
      </Stack>
    </AppCard>
  );
}
